import { memo, useCallback, useMemo, useState } from "react"
import { useQuery } from "@rocicorp/zero/react"

import type { FeedbackOrigin } from "@productlane/db/browser"
import type { DateRangeType } from "@productlane/ui"
import {
	DateRangeSelector,
	ProductlaneIcon,
	ScrollArea,
	ScrollViewport,
	Tabs,
	TabsContent,
	TabsList,
	TabsTrigger,
} from "@productlane/ui"
import { queries } from "@productlane/zero/queries"

import type { ReportingFilters } from "./reporting-filters"
import { MetaInfo } from "@/components/meta-info"
import { RoleCheck } from "@/components/shared/role-check"
import { useIsProductlaneWorkspace } from "@/lib/hooks/use-is-productlane-workspace"
import { useReportingState } from "@/lib/states/global-state"
import { PageHeader } from "../page-header"
import { ReportingFeedbackButton } from "./reporting-feedback-button"
import {
	ReportingFilterButton,
	ReportingFilterChips,
	toReportingFilterInput,
} from "./reporting-filters"
import { getReportingTabs, normalizeReportingTab } from "./reporting-tabs"
import { ConversationsTab } from "./tabs/conversations-tab"
import { FeedbackTab } from "./tabs/feedback-tab"
import { OverviewTab } from "./tabs/overview-tab"
import { ResponsivenessTab } from "./tabs/responsiveness-tab"
import { SlasTab } from "./tabs/slas-tab"
import { TeamTab } from "./tabs/team-tab"

const RANGE_DAYS: Record<string, number> = {
	"7days": 7,
	"4weeks": 28,
	"3months": 90,
	"12months": 365,
}

function getInitialDateRange(
	rangeType: string,
	customStartDate: string | null,
	customEndDate: string | null,
) {
	if (rangeType === "custom" && customStartDate && customEndDate) {
		return {
			startDate: new Date(customStartDate),
			endDate: new Date(customEndDate),
		}
	}
	const days = RANGE_DAYS[rangeType]
	if (!days) return { startDate: undefined, endDate: undefined }
	const now = new Date()
	return {
		startDate: new Date(now.getTime() - days * 24 * 60 * 60 * 1000),
		endDate: now,
	}
}

export const ReportingScreen = memo(() => {
	const {
		activeTab,
		setActiveTab,
		rangeType,
		setRangeType,
		customStartDate,
		customEndDate,
		setCustomDateRange,
		filters: persistedFilters,
		setFilters,
	} = useReportingState()

	// Undefined while Zero loads the workspace, and a gated tab stays hidden then.
	const isProductlaneWorkspace = useIsProductlaneWorkspace() === true
	const visibleTabs = useMemo(
		() => getReportingTabs({ isProductlaneWorkspace }),
		[isProductlaneWorkspace],
	)

	const [teamMembers] = useQuery(queries.teamMembers())

	const filters = useMemo<ReportingFilters>(
		() => ({
			origins: (persistedFilters.origins ?? []) as FeedbackOrigin[],
			assigneeIds: persistedFilters.assigneeIds ?? [],
			tagIds: persistedFilters.tagIds ?? [],
			companyIds: persistedFilters.companyIds ?? [],
		}),
		[persistedFilters],
	)

	const handleFiltersChange = useCallback(
		(next: ReportingFilters) => {
			setFilters(next)
		},
		[setFilters],
	)

	const filterInput = useMemo(() => toReportingFilterInput(filters), [filters])

	const [dateRange, setDateRange] = useState<{
		startDate: Date | undefined
		endDate: Date | undefined
	}>(() => getInitialDateRange(rangeType, customStartDate, customEndDate))

	const handleDateRangeChange = useCallback(
		(startDate: Date | undefined, endDate: Date | undefined) => {
			setDateRange({ startDate, endDate })
			if (startDate && endDate) {
				setCustomDateRange(startDate, endDate)
			}
		},
		[setCustomDateRange],
	)

	const handleRangeTypeChange = useCallback(
		(newRangeType: DateRangeType) => {
			setRangeType(newRangeType)
		},
		[setRangeType],
	)

	const isShowingDateRange = rangeType !== "all"

	const initialCustomRange =
		customStartDate && customEndDate
			? { from: new Date(customStartDate), to: new Date(customEndDate) }
			: undefined

	const tabValue = normalizeReportingTab(activeTab, {
		isProductlaneWorkspace,
	})

	const queryInput = useMemo(
		() => ({
			startDate: dateRange.startDate,
			endDate: dateRange.endDate,
			...filterInput,
		}),
		[dateRange.startDate, dateRange.endDate, filterInput],
	)

	const hasFilters =
		filters.origins.length > 0 ||
		filters.assigneeIds.length > 0 ||
		filters.tagIds.length > 0 ||
		filters.companyIds.length > 0

	return (
		<RoleCheck not={["VIEWER"]}>
			<MetaInfo title="Reporting" />
			<Tabs
				value={tabValue}
				onValueChange={(v) => {
					setActiveTab(normalizeReportingTab(v, { isProductlaneWorkspace }))
				}}
				className="flex min-h-0 flex-1 flex-col"
			>
				<div
					className={
						isShowingDateRange
							? "sticky top-0 z-1 bg-white dark:bg-gray-900"
							: undefined
					}
				>
					<PageHeader
						title="Reporting"
						clearsNavToggle
						actions={
							<div className="flex items-center gap-1">
								<ReportingFeedbackButton />
								<ReportingFilterButton
									filters={filters}
									onFiltersChange={handleFiltersChange}
									teamMembers={teamMembers}
								/>
								<DateRangeSelector
									onChange={handleDateRangeChange}
									onRangeTypeChange={handleRangeTypeChange}
									initialRangeType={rangeType}
									initialCustomRange={initialCustomRange}
								/>
							</div>
						}
					/>
					<div className="flex shrink-0 items-center overflow-x-auto border-b border-gray-200 px-4 py-2.5 sm:px-5 lg:px-6 dark:border-gray-700/80">
						<TabsList className="w-max min-w-0">
							{visibleTabs.map((tab) => (
								<TabsTrigger key={tab.value} value={tab.value}>
									<span className="inline-flex items-center gap-1.5">
										{tab.productlaneOnly ? (
											<ProductlaneIcon
												className="h-3 w-3 shrink-0 text-tertiary"
												fill="currentColor"
											/>
										) : null}
										{tab.label}
									</span>
								</TabsTrigger>
							))}
						</TabsList>
					</div>
					{hasFilters ? (
						<div className="border-b border-gray-200 px-4 py-2 sm:px-5 lg:px-6 dark:border-gray-700/80">
							<ReportingFilterChips
								filters={filters}
								onChange={handleFiltersChange}
								teamMembers={teamMembers}
							/>
						</div>
					) : null}
				</div>

				<div className="min-h-0 flex-1 bg-white dark:bg-gray-900">
					<ScrollArea>
						<ScrollViewport>
							<div className="select-none pb-6">
								<TabsContent value="overview">
									<OverviewTab {...queryInput} />
								</TabsContent>
								<TabsContent value="conversations">
									<ConversationsTab {...queryInput} />
								</TabsContent>
								<TabsContent value="team">
									<TeamTab {...queryInput} />
								</TabsContent>
								<TabsContent value="responsiveness">
									<ResponsivenessTab {...queryInput} />
								</TabsContent>
								{isProductlaneWorkspace ? (
									<TabsContent value="slas">
										<SlasTab {...queryInput} />
									</TabsContent>
								) : null}
								<TabsContent value="feedback">
									<FeedbackTab {...queryInput} />
								</TabsContent>
							</div>
						</ScrollViewport>
					</ScrollArea>
				</div>
			</Tabs>
		</RoleCheck>
	)
})
